/**
 * Menu da conta — o "eu sou fulano" que o modo classe nunca teve.
 *
 * Diagnóstico de 06/08 (.planning/CONTAS-E-DASHBOARD-2026-08-06.md §2.1–2.2):
 * nenhuma tela do modo classe mostrava quem está logado nem tinha um botão de
 * sair. A mesma tela serve professor, coordenador, diretor e superadmin — e
 * nada dizia qual deles você é. Num notebook de escola, compartilhado, a
 * sessão ficava aberta pra sempre.
 *
 * Registro: product (DESIGN.md) — o menu é mobília do console, não adesivo.
 * Nome + papel ficam VISÍVEIS no gatilho (em tela md+): "um superadmin e um
 * diretor logados lado a lado se distinguem pela tela", sem clique. O clique
 * abre e-mail, escola, perfil, trocar senha e Sair.
 *
 * A leitura de quem-sou-eu mora no `useIdentidade` — a mesma do /perfil.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useIdentidade, iniciaisDe } from '@/hooks/useIdentidade';
import { TEM_MENU_LATERAL } from '@/lib/navegacao';

export function MenuDaConta({ noPeDoMenu = false }: { noPeDoMenu?: boolean } = {}) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { uid, email, nome, avatarUrl, rotulo, escolaNome } = useIdentidade();
  const [saindo, setSaindo] = useState(false);

  /* QUEM É O DONO DA IDENTIDADE NA TELA (12/08).
     Nove páginas chamam este componente no próprio cabeçalho — era o único
     lugar que dizia quem está logado. Com o menu lateral, a conta passou pro PÉ
     do menu, e manter as duas deixaria dois avatares na mesma tela.
     A guarda mora aqui, e não nas nove páginas, porque mudar navegação e mexer
     em nove telas que funcionam no mesmo passo é exatamente o que a regra de
     refatoração do projeto proíbe. Marca sem menu lateral não vê diferença. */
  if (TEM_MENU_LATERAL && !noPeDoMenu) return null;

  if (!uid) return null;

  const nomeExibido = nome ?? email ?? '';
  /* Superadmin é global (sem escola); pra ele o rótulo fica sozinho. */
  const papelEEscola = rotulo
    ? [rotulo, escolaNome].filter(Boolean).join(' · ')
    : null;

  const sair = async () => {
    setSaindo(true);
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 gap-2 px-1.5 md:px-2"
          aria-label={`Conta de ${nomeExibido}`}
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={avatarUrl ?? undefined} alt="" />
            <AvatarFallback className="bg-primary/10 text-xs font-medium text-primary">
              {iniciaisDe(nomeExibido)}
            </AvatarFallback>
          </Avatar>
          {/* Visível sem clique em tela larga; no celular o avatar basta. */}
          <span className="hidden flex-col items-start leading-tight md:flex">
            <span className="max-w-40 truncate text-sm font-medium">{nomeExibido}</span>
            {papelEEscola && (
              <span className="max-w-40 truncate text-[11px] text-muted-foreground">
                {papelEEscola}
              </span>
            )}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-medium">{nomeExibido}</p>
          {email && (
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          )}
          {papelEEscola && (
            <p className="mt-1 text-xs text-muted-foreground">{papelEEscola}</p>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => navigate('/perfil')}
        >
          <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
          Meu perfil
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => navigate('/trocar-senha')}
        >
          <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
          Trocar minha senha
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer text-destructive focus:text-destructive"
          disabled={saindo}
          onClick={sair}
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          {saindo ? 'Saindo…' : 'Sair'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
